/**
 * The Genre Mixer's live prompt generator: the editable textarea plus its three
 * actions.
 *
 * The prompt is rebuilt from the mixer settings on a debounce, unless the user has
 * typed into it — that latch lives in mixerStore (`promptDirty`), not here, so this
 * panel stays a controlled textarea.
 *
 * "Save Prompt" is SIMULATED: it toasts and nothing is stored. It is kept because
 * the prototype had it and a real prompt library is a backend feature.
 */
import { Check, Copy, RefreshCw } from 'lucide-react';
import { Btn, PrototypeNotice } from '../ui';
import { c } from '../../styles/theme';
import SelectedBonusPacks from '../bonus/SelectedBonusPacks';

export interface MixerPromptPanelProps {
  prompt: string;
  extraDescription: string;
  onChange: (value: string) => void;
  onExtraDescriptionChange: (value: string) => void;
  onCopy: () => void;
  onRegenerate: () => void;
  onSave: () => void;
}

export default function MixerPromptPanel({
  prompt,
  extraDescription,
  onChange,
  onExtraDescriptionChange,
  onCopy,
  onRegenerate,
  onSave,
}: MixerPromptPanelProps) {
  return (
    <>
      <label
        htmlFor="gm-prompt"
        style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 8px' }}
      >
        AI-generated prompt (editable)
      </label>
      <SelectedBonusPacks />
      <textarea
        id="gm-prompt"
        rows={4}
        value={prompt}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          resize: 'none',
          fontSize: 13,
          padding: 12,
          color: c.text1,
          marginBottom: 10,
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        <Btn variant="secondary" onClick={onCopy}>
          <Copy size={13} />
          Copy Prompt
        </Btn>
        <Btn variant="secondary" onClick={onRegenerate}>
          <RefreshCw size={13} />
          Regenerate Prompt
        </Btn>
        <Btn variant="secondary" onClick={onSave}>
          <Check size={13} />
          Save Prompt
        </Btn>
      </div>

      <label
        htmlFor="gm-extra-description"
        style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 8px' }}
      >
        Add anything you want to hear, feel, or change about your song...
      </label>
      <textarea
        id="gm-extra-description"
        rows={3}
        required
        aria-required={true}
        value={extraDescription}
        onChange={(e) => onExtraDescriptionChange(e.target.value)}
        style={{
          width: '100%',
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          resize: 'vertical',
          fontSize: 13,
          padding: 12,
          color: c.text1,
          marginBottom: 12,
          boxSizing: 'border-box',
        }}
      />
      <PrototypeNotice style={{ marginTop: 0, marginBottom: 18 }}>
        <strong style={{ color: c.text1, fontWeight: 600 }}>Tip:</strong> You don't need to repeat
        the genre or style. Just describe what you want to add.
      </PrototypeNotice>
    </>
  );
}
